"use client";

import {
  type ChangeEvent,
  type FC,
  Fragment,
  type HTMLAttributes,
  type MouseEvent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import { motion, AnimatePresence } from "motion/react";
import type { RecipeVariants } from "@vanilla-extract/recipes";
import { mergeClasses } from "@huub-web/utils";
import { useTranslation } from "react-i18next";

import type { ClassNameProp } from "../../props";
import { Edges } from "../Edges";
import { Flex } from "../Flex";
import { IconButton } from "../IconButton";
import type { IconName } from "../../theme/types/IconName";
import {
  PlainTextArea,
  type HTMLTextAreaElementWithReset,
  type TextAreaProps,
} from "../PlainTextArea";
import { Container } from "../Container";
import { useInitializedEffect } from "../../hooks/useInitializedEffect";
import { useMaybePubSub } from "../../hooks/usePubSub";
import { UserPrompt } from "../UserPrompt";
import { Tooltip } from "../Tooltip";
import { transitionDurations } from "../../theme/transitionDurations";
import { Stack } from "../Stack";
import type { UserPromptRecord } from "../../types";
import { Suggestion } from "../Suggestion";

import {
  taskbarStyle,
  containerStyle,
  textAreaStyle,
  suggestionsFlexStyle,
  suggestionsOverflowStyle,
  userMessageFlexStyle,
  edgeContentStyle,
  actionsStyle,
  messagesStackStyle,
  userMessageContainerStyle,
} from "./TaskBar.css";
import { elevations } from "../../theme/css/elevations.css";

export type TaskBarProps = HTMLAttributes<HTMLDivElement> &
  ClassNameProp &
  RecipeVariants<typeof taskbarStyle> & {
    actions?: ReactNode;
    microphoneDisabled?: boolean;
    isRecording?: boolean;
    minified?: boolean;
    placeholder?: string;
    promptType?: "text" | "speech" | "help";
    prompt?: string;
    suggestions?: UserPromptRecord[];
    defaultValue?: string;
    disabled?: boolean;
    locked?: boolean;
    trailing?: ReactNode;
    transcription?: string;
    immediate?: boolean;
    state?: "idle" | "waiting";
    onMessage?: (message: string) => void;
    onMessageStop?: () => void;
    onRecord?: () => void;
    onRecordStop?: () => void;
    onSuggestion?: (suggestion: UserPromptRecord) => void;
  };

/**
 * The TaskBar component is a component that allows the user to send messages to the AI, and control the app.
 */
export const TaskBar: FC<TaskBarProps> = ({
  variant = "canvas",
  size,
  placeholder,
  microphoneDisabled = false,
  isRecording = false,
  defaultValue,
  suggestions,
  prompt,
  locked = false,
  disabled = false,
  actions,
  autoFocus = false,
  trailing,
  transcription,
  promptType = "text",
  immediate = false,
  state,
  className,
  onRecord,
  onRecordStop,
  onMessageStop,
  onMessage,
  onSuggestion,
  children,
  ...props
}) => {
  const { t } = useTranslation();
  const pubSub = useMaybePubSub();

  const [isFocused, setIsFocused] = useState(false);
  const [value, setValue] = useState(defaultValue ?? "");
  const [hasRecentInput, setHasRecentInput] = useState(
    (defaultValue?.length ?? 0) > 0,
  );
  const textareaRef = useRef<HTMLTextAreaElementWithReset>(null);

  const placeholderText = (() => {
    if (placeholder) {
      return placeholder;
    }
    if (isRecording) {
      return t("taskbar.audio.placeholder");
    }
    return t("taskbar.message.placeholder");
  })();

  const mainButtonIcon: IconName = (() => {
    if (locked) {
      return "stop";
    }
    if (hasRecentInput) {
      return "arrowUp";
    }
    if (microphoneDisabled) {
      return "microphoneOffFilled";
    }
    if (isRecording) {
      return "microphoneOffFilled";
    }
    return "microphoneFilled";
  })();

  const messageIcon: IconName = (() => {
    if (promptType === "help") {
      return "ai";
    }
    return promptType === "text" ? "transcribe" : "sound";
  })();

  const tooltip: ReactNode | undefined = (() => {
    if (disabled || locked) {
      return undefined;
    }
    if (isRecording) {
      return t("taskbar.audio.tooltip.stop");
    }
    if (hasRecentInput) {
      return t("taskbar.message.tooltip.send");
    }
    if (microphoneDisabled) {
      return t("taskbar.microphonePermissonsDenied.tooltip");
    }
    return t("taskbar.audio.tooltip.start");
  })();

  // Handlers
  // ---------

  const handleInputFocus = () => {
    setIsFocused(true);

    if (value) {
      setHasRecentInput(true);
    }
  };

  const handleInputBlur = () => {
    setIsFocused(false);
  };

  const handleInputChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value);
    setHasRecentInput(e.target.value.length > 0);
  };

  const handleTaskBarClick = (e: MouseEvent<HTMLDivElement>) => {
    if (
      !disabled &&
      ((e.target as HTMLElement)?.id === "taskbar" ||
        (e.target as HTMLElement)?.classList.contains("--actions") ||
        (e.target as HTMLElement).children?.[0]?.tagName === "TEXTAREA") &&
      !!textareaRef.current
    ) {
      textareaRef.current?.focus();
    }
  };

  const handleMainActionButtonClick = () => {
    if (isRecording) {
      onRecordStop?.();
    } else if (locked) {
      onMessageStop?.();
    } else if (hasRecentInput && value) {
      sendMessage(value);
    } else {
      onRecord?.();
    }
  };

  const handleEnterKey: TextAreaProps["onEnterKey"] = () => {
    if (value && !isRecording) {
      sendMessage(value);
    }
  };

  const sendMessage = (message: string) => {
    if (locked) {
      return;
    }

    onMessage?.(message);

    if (textareaRef.current) {
      textareaRef.current.reset();
      setValue("");
      setHasRecentInput(false);
    }
  };

  useInitializedEffect(() => {
    setValue(transcription ?? "");
  }, [transcription]);

  // Focussing
  // ---------

  useEffect(() => {
    if (!pubSub) {
      return;
    }

    const unsubscribe = pubSub.subscribe("focusTaskbar", () => {
      textareaRef.current?.focus();
    });

    return () => {
      unsubscribe();
    };
  }, [pubSub]);

  const ContainerComponent = variant === "canvas" ? Container : Fragment;

  return (
    <AnimatePresence>
      <motion.div
        className={containerStyle({ variant })}
        initial={{ opacity: 0, y: variant === "canvas" ? 20 : 0 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: variant === "canvas" ? 20 : 0 }}
        transition={{
          duration: immediate ? 0 : 0.2,
          delay: immediate ? 0 : 0.75,
        }}
      >
        <Stack
          className={messagesStackStyle({ variant })}
          data-stack="taskbar-stack"
          overflowHidden
        >
          <AnimatePresence>
            {!disabled && suggestions && suggestions.length > 0 && !prompt && (
              <motion.div
                className={suggestionsOverflowStyle({ variant })}
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -10, opacity: 0, pointerEvents: "none" }}
                transition={{ duration: transitionDurations.m }}
              >
                <Flex
                  className={suggestionsFlexStyle({ variant })}
                  align="center"
                  justify="center"
                  inline
                >
                  {suggestions?.map((suggestion, i) => (
                    <Suggestion
                      key={i}
                      suggestion={suggestion}
                      onClick={onSuggestion}
                      variant={variant}
                    />
                  ))}
                </Flex>
              </motion.div>
            )}
          </AnimatePresence>

          {variant === "canvas" && (
            <AnimatePresence>
              {prompt && (
                <motion.div
                  initial={{ y: 10, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: -10, opacity: 0, pointerEvents: "none" }}
                  transition={{ duration: transitionDurations.m }}
                  className={userMessageFlexStyle}
                >
                  <Container className={userMessageContainerStyle}>
                    <Flex align="center" justify="center" inline>
                      <UserPrompt icon={messageIcon}>{prompt}</UserPrompt>
                    </Flex>
                  </Container>
                </motion.div>
              )}
            </AnimatePresence>
          )}
        </Stack>

        <ContainerComponent>
          {/* biome-ignore lint/a11y/useSemanticElements: taskbar container wraps textarea and interactive children, cannot be a button element */}
          <div
            id="taskbar"
            data-state={state}
            role="button"
            tabIndex={0}
            onClick={handleTaskBarClick}
            onKeyDown={(e) => {
              if (e.target !== e.currentTarget) return;
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleTaskBarClick(e as unknown as MouseEvent<HTMLDivElement>);
              }
            }}
            className={mergeClasses(
              taskbarStyle({ size, variant, focused: isFocused, disabled }),
              isFocused && variant === "canvas" ? elevations.l : undefined,
              className,
            )}
            {...props}
          >
            <Edges
              className={edgeContentStyle({})}
              trailing={
                <Tooltip placement="left" value={tooltip}>
                  <IconButton
                    icon={mainButtonIcon}
                    variant="secondary"
                    disabled={disabled}
                    onClick={handleMainActionButtonClick}
                  />
                </Tooltip>
              }
            >
              {children}

              <Flex direction="column" gap="xxs" grow={false}>
                <PlainTextArea
                  ref={textareaRef}
                  className={textAreaStyle}
                  placeholder={placeholderText}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                  onChange={handleInputChange}
                  onEnterKey={handleEnterKey}
                  disabled={disabled || isRecording}
                  value={value}
                  autoFocus={autoFocus}
                />
                {actions && (
                  <Flex
                    gap="xxs"
                    className={mergeClasses(actionsStyle({}), "--actions")}
                    grow={false}
                  >
                    {actions}
                  </Flex>
                )}
              </Flex>
            </Edges>
          </div>
        </ContainerComponent>

        {trailing}
      </motion.div>
    </AnimatePresence>
  );
};
